import React, { useContext } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { getAiRolePlay } from 'redux/selectors/timeline';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';

import { Button } from 'react-bootstrap';
import NvIcon from 'shared/components/nv-icon';
import { doubleSpacing } from 'styles/global_defaults/scaffolding';
import { config } from '../../../../../config/pendo.config.json';
import AiConversationContext, { AIRolePlayStatus, VoiceRecordingState } from './context';
import useAiRolePlayHelper from '../hooks/use-ai-role-play-helper';
import RolePlayTitle from './role-play-title';
import RecordingStatus from './voice-mode/recording-status';
import useViewedSubmission from './use-viewed-submission';
import { ClosingReason } from './stream-ai-response';

const ConversationModalHeader = () => {
  const dispatch = useDispatch();
  const {
    aiRolePlayStatus,
    aiRolePlayId,
    handleExitChat,
    isRolePlayStarting,
    handleStartConversation,
    interactionMode,
    voiceRecordingState,
  } = useContext(AiConversationContext);

  const { isMobile } = useAiRolePlayHelper();
  const aiRolePlay = useSelector((state) => getAiRolePlay(state, aiRolePlayId));
  const {
    viewedSubmission: submission,
  } = useViewedSubmission();

  const { closingReason } = submission || {};
  const { title } = aiRolePlay || {};

  const showRecordingStatus = interactionMode === RolePlayInteractionMode.VOICE
    && voiceRecordingState !== VoiceRecordingState.IDLE
    && closingReason !== ClosingReason.TERMINATED;

  const styles = css`
    display: grid;
    grid-template-columns: ${showRecordingStatus ? '40% 20% 40%' : '70% 30%'};
    min-height: 0;

    .header-left {
      min-width: 0;
      overflow: hidden;
    }

    .header-right {
      justify-content: flex-end;
    }

    .practice-again-button {
      height: ${doubleSpacing}px;
    }
  `;

  const handleStartOver = () => {
    dispatch(openConfirmationDialog({
      title: t.FORM.ARE_YOU_SURE(),
      bodyText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER_CONFIRMATION.MESSAGE(),
      confirmText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER_CONFIRMATION.CONFIRM(),
      cancelText: t.FORM.CANCEL(),
      onConfirm: handleStartConversation,
    }));
  };

  return (
    <div css={styles} className={`border-bottom align-items-center ${isMobile ? 'px-4' : 'px-6'}`}>
      {/* Left: role-play title */}
      <div className='header-left d-flex align-items-center'>
        <RolePlayTitle title={title} />
      </div>

      {/* Center: recording status (voice mode only) */}
      {showRecordingStatus && (
        <div className='header-center d-flex justify-content-center align-items-center'>
          <RecordingStatus recordingState={voiceRecordingState} />
        </div>
      )}

      {/* Right: action buttons */}
      <div className='header-right d-flex py-4 align-items-center'>
        {(
          ((closingReason === ClosingReason.TERMINATED || aiRolePlayStatus === AIRolePlayStatus.FAILED)
            && interactionMode === RolePlayInteractionMode.VOICE)
          || aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS) && (
          <>
            <Button
              className='bg-transparent shadow-none border-0 text-gray-1 d-flex px-0'
              onClick={handleStartOver}
              disabled={isRolePlayStarting}
              aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER()}
              data-qa={config.pendo.activities.aiRolePlay.conversationModal.startOver}
            >
              <NvIcon
                icon='refresh'
                size='small'
                className='d-flex align-items-center text-gray-2'
              />
              {!isMobile && (
                <p className='mb-0 ml-2 text-gray-1'>
                  {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER()}
                </p>
              )}
            </Button>
            <Button
              variant='danger'
              className={isMobile ? 'ml-4' : 'ml-5'}
              onClick={() => handleExitChat()}
              aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_ROLEPLAY()}
              data-qa={config.pendo.activities.aiRolePlay.conversationModal.exitChat}
            >
              {isMobile
                ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT()
                : t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_ROLEPLAY()}
            </Button>
          </>
        )}
        {aiRolePlayStatus === AIRolePlayStatus.COMPLETED
          && (interactionMode === RolePlayInteractionMode.TEXT || closingReason !== ClosingReason.TERMINATED)
          && (
            <Button
              variant='primary'
              onClick={() => handleExitChat()}
              data-qa={config.pendo.activities.aiRolePlay.conversationModal.close}
            >
              {t.FORM.CLOSE()}
            </Button>
          )}
      </div>
    </div>
  );
};

export default ConversationModalHeader;
